import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Row,
  Col,
  Button,
  Space,
  Table,
  Tag,
  Alert,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  message,
  Spin,
  Statistic,
  Progress,
  Badge,
  Grid,
  Dropdown,
} from 'antd';
import {
  PlusOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  RiseOutlined,
  DashboardOutlined,
  FileTextOutlined,
  SafetyOutlined,
  MoreOutlined,
  FlagOutlined,
  AuditOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ProjectSelector from '../../../components/common/ProjectSelector';
import { useProject } from '../../../contexts/ProjectContext';
import { useAuth } from '../../../contexts/AuthContext';
import { authService } from '../../../services/authService';
import { User } from '../../../types';
import {
  riskService,
  Risk,
  Mitigation,
  RiskSeverity,
  RiskStatus,
  MitigationStatus,
  EscalationLevel,
  RiskMatrix as RiskMatrixType,
  RiskMonitoring as RiskMonitoringType,
} from '../../../services/riskService';
import { aiService } from '../../../services/aiService';
import { scheduleService, ScheduleTask } from '../../../services/scheduleService';
import RiskMatrix from '../../../components/Charts/RiskMatrix';
import AIConfidenceBadge from '../../../components/ai/AIConfidenceBadge';

const { useBreakpoint } = Grid;

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ProjectRisk: React.FC = () => {
  const { selectedProject, isLoading: projectsLoading } = useProject();
  const { can } = useAuth();
  const screens = useBreakpoint();
  const isMobile = !screens.sm;
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('risks');

  // Data states
  const [risks, setRisks] = useState<Risk[]>([]);
  const [mitigations, setMitigations] = useState<Mitigation[]>([]);
  const [riskMatrix, setRiskMatrix] = useState<RiskMatrixType | null>(null);
  const [monitoring, setMonitoring] = useState<RiskMonitoringType | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [scheduleTasks, setScheduleTasks] = useState<ScheduleTask[]>([]);
  const [aiScoringLoading, setAiScoringLoading] = useState(false);
  const [aiReviewedRiskIds, setAiReviewedRiskIds] = useState<Record<string, boolean>>({});

  // Modal states
  const [riskModalVisible, setRiskModalVisible] = useState(false);
  const [mitigationModalVisible, setMitigationModalVisible] = useState(false);
  const [escalateModalVisible, setEscalateModalVisible] = useState(false);
  const [assessModalVisible, setAssessModalVisible] = useState(false);
  const [editingRisk, setEditingRisk] = useState<Risk | null>(null);
  const [selectedRisk, setSelectedRisk] = useState<Risk | null>(null);

  // Forms
  const [riskForm] = Form.useForm();
  const [mitigationForm] = Form.useForm();
  const [escalateForm] = Form.useForm();
  const [assessForm] = Form.useForm();

  useEffect(() => {
    authService.getUsers().then(u => setUsers(Array.isArray(u) ? u : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedProject) {
      // Clear stale data when switching projects
      setRisks([]);
      setMitigations([]);
      setRiskMatrix(null);
      setMonitoring(null);
      setScheduleTasks([]);
      loadRiskData();
    }
  }, [selectedProject]);



  const loadRiskData = async () => {
    if (!selectedProject) return;

    setLoading(true);
    try {
      const [risksData, mitigationsData, matrixData, monitoringData, tasksData] = await Promise.allSettled([
        riskService.getRisks(selectedProject.id),
        riskService.getMitigations(selectedProject.id),
        riskService.getRiskMatrix(selectedProject.id),
        riskService.getRiskMonitoring(selectedProject.id),
        scheduleService.getProjectTasks(selectedProject.id),
      ]);

      if (risksData.status === 'fulfilled') setRisks(risksData.value.risks);
      if (mitigationsData.status === 'fulfilled') setMitigations(mitigationsData.value.mitigations);
      if (matrixData.status === 'fulfilled' && matrixData.value) setRiskMatrix(matrixData.value);
      if (monitoringData.status === 'fulfilled' && monitoringData.value) setMonitoring(monitoringData.value);
      if (tasksData.status === 'fulfilled') setScheduleTasks(Array.isArray(tasksData.value) ? tasksData.value : []);
    } catch (error: any) {
      console.error('Error loading risk data:', error);
      message.error('Failed to load risk data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadRiskData();
  };

  const hasAiSuggestion = (risk: Risk): boolean => {
    const r: any = risk as any;
    return r.aiGeneratedAt != null || r.aiConfidence != null || r.aiSeverity != null;
  };

  const markAiReviewed = (riskId: string) => {
    setAiReviewedRiskIds(prev => ({ ...prev, [riskId]: true }));
  };

  const handleRunAiRiskScoring = async () => {
    if (!selectedProject) return;
    setAiScoringLoading(true);
    try {
      await aiService.runRiskScoring(selectedProject.id);
      message.success('AI risk scoring completed. Review suggestions below.');
      setAiReviewedRiskIds({});
      await loadRiskData();
    } catch (error: any) {
      message.error(error?.message || 'Failed to run AI risk scoring');
    } finally {
      setAiScoringLoading(false);
    }
  };

  const handleApplyAiSuggestion = async (risk: Risk) => {
    const r: any = risk as any;
    const aiProbability = r.aiProbability;
    const aiImpact = r.aiImpact;
    const aiSeverity = r.aiSeverity;
    const aiRiskScore = r.aiRiskScore;

    if (aiProbability == null && aiImpact == null && aiSeverity == null && aiRiskScore == null) {
      message.info('No AI suggestion values found for this risk.');
      return;
    }

    try {
      const payload: any = {};
      if (typeof aiProbability === 'number') payload.probability = aiProbability;
      if (typeof aiImpact === 'number') payload.impact = aiImpact;
      if (typeof aiSeverity === 'string') payload.severity = aiSeverity;
      if (typeof aiRiskScore === 'number') payload.riskScore = aiRiskScore;

      await riskService.updateRisk(risk.id, payload);
      message.success('AI suggestion applied to baseline risk fields.');
      markAiReviewed(risk.id);
      await loadRiskData();
    } catch (error: any) {
      message.error(error?.message || 'Failed to apply AI suggestion');
    }
  };

  const handleKeepBaseline = (risk: Risk) => {
    markAiReviewed(risk.id);
    message.info('Kept baseline risk values (AI suggestion dismissed for review).');
  };

  const handleAddRisk = () => {
    setEditingRisk(null);
    riskForm.resetFields();
    setRiskModalVisible(true);
  };

  const handleEditRisk = (risk: Risk) => {
    setEditingRisk(risk);
    riskForm.setFieldsValue({
      title: risk.title,
      description: risk.description,
      status: risk.status,
      severity: risk.severity,
      probability: risk.probability,
      impact: risk.impact,
      responseStrategy: risk.responseStrategy,
      linkedTaskIds: (risk as any).linkedTaskIds ?? (risk as any).linked_task_ids ?? (risk as any).taskIds ?? [],
      scheduleImpactDays: Number((risk as any).scheduleImpactDays ?? (risk as any).delayDays ?? (risk as any).delay_days ?? 0),
    });
    setRiskModalVisible(true);
  };

  const handleDeleteRisk = async (riskId: string) => {
    Modal.confirm({
      title: 'Delete Risk',
      content: 'Are you sure you want to delete this risk? This will also delete all associated mitigations.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await riskService.deleteRisk(riskId);
          message.success('Risk deleted successfully');
          loadRiskData();
        } catch (error: any) {
          message.error(error.message || 'Failed to delete risk');
        }
      },
    });
  };

  const handleRiskSubmit = async () => {
    try {
      const values = await riskForm.validateFields();
      const riskData: any = {
        title: values.title,
        description: values.description,
        probability: values.probability,
        impact: values.impact,
        status: values.status,
        severity: values.severity,
        responseStrategy: values.responseStrategy,
        projectId: selectedProject?.id,
        linkedTaskIds: Array.isArray(values.linkedTaskIds) ? values.linkedTaskIds : [],
        scheduleImpactDays: Number(values.scheduleImpactDays ?? 0),
      };

      if (editingRisk) {
        await riskService.updateRisk(editingRisk.id, riskData);
        message.success('Risk updated successfully');
      } else {
        await riskService.createRisk(riskData);
        message.success('Risk created successfully');
      }

      setRiskModalVisible(false);
      riskForm.resetFields();
      loadRiskData();
    } catch (error: any) {
      message.error(error.message || 'Failed to save risk');
    }
  };

  const handleAssessRisk = (risk: Risk) => {
    setSelectedRisk(risk);
    assessForm.setFieldsValue({
      residualProbability: risk.residualProbability || risk.probability,
      residualImpact: risk.residualImpact || risk.impact,
    });
    setAssessModalVisible(true);
  };

  const handleAssessSubmit = async () => {
    try {
      const values = await assessForm.validateFields();
      if (!selectedRisk) return;

      await riskService.assessRisk(selectedRisk.id, values.residualProbability, values.residualImpact);
      message.success('Risk assessed successfully');
      setAssessModalVisible(false);
      assessForm.resetFields();
      loadRiskData();
    } catch (error: any) {
      message.error(error.message || 'Failed to assess risk');
    }
  };

  const handleEscalateRisk = (risk: Risk) => {
    setSelectedRisk(risk);
    escalateForm.resetFields();
    setEscalateModalVisible(true);
  };

  const handleEscalateSubmit = async () => {
    try {
      const values = await escalateForm.validateFields();
      if (!selectedRisk) return;

      await riskService.escalateRisk(selectedRisk.id, values.level, values.reason);
      message.success('Risk escalated successfully');
      setEscalateModalVisible(false);
      escalateForm.resetFields();
      loadRiskData();
    } catch (error: any) {
      message.error(error.message || 'Failed to escalate risk');
    }
  };

  const handleAddMitigation = (risk?: Risk) => {
    if (risk) {
      mitigationForm.setFieldsValue({ riskId: risk.id });
      setSelectedRisk(risk);
    } else {
      mitigationForm.resetFields();
    }
    setMitigationModalVisible(true);
  };

  const handleMitigationSubmit = async () => {
    try {
      const values = await mitigationForm.validateFields();

      const mitigationData = {
        ...values,
        startDate: values.startDate?.toISOString(),
        targetDate: values.targetDate?.toISOString(),
      };

      await riskService.createMitigation(mitigationData);
      message.success('Mitigation created successfully');
      setMitigationModalVisible(false);
      mitigationForm.resetFields();
      loadRiskData();
    } catch (error: any) {
      message.error(error.message || 'Failed to create mitigation');
    }
  };

  const handleDeleteMitigation = async (mitigationId: string) => {
    try {
      await riskService.deleteMitigation(mitigationId);
      message.success('Mitigation deleted successfully');
      loadRiskData();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete mitigation');
    }
  };

  // Helper functions — API returns decimals (0.1, 0.25, 0.5, 0.75, 1.0)
  const getDecimalLabel = (val: number): string => {
    if (val <= 0.1) return 'Very Low';
    if (val <= 0.25) return 'Low';
    if (val <= 0.5) return 'Medium';
    if (val <= 0.75) return 'High';
    return 'Very High';
  };

  const getDecimalColor = (val: number): string => {
    if (val <= 0.25) return 'green';
    if (val <= 0.5) return 'orange';
    return 'red';
  };

  const getProbabilityLabel = (prob: number): string => getDecimalLabel(prob);
  const getImpactLabel = (impact: number): string => getDecimalLabel(impact);

  const getSeverityColor = (severity: RiskSeverity): string => {
    const colors = {
      [RiskSeverity.LOW]: 'green',
      [RiskSeverity.MEDIUM]: 'orange',
      [RiskSeverity.HIGH]: 'red',
      [RiskSeverity.CRITICAL]: 'purple',
    };
    return colors[severity] || 'default';
  };

  const getStatusColor = (status: RiskStatus): string => {
    const colors = {
      [RiskStatus.IDENTIFIED]: 'blue',
      [RiskStatus.ANALYZED]: 'cyan',
      [RiskStatus.MITIGATING]: 'green',
      [RiskStatus.MONITORING]: 'orange',
      [RiskStatus.CLOSED]: 'default',
      [RiskStatus.ESCALATED]: 'red',
    };
    return colors[status] || 'default';
  };

  // Calculate statistics
  const safeRisks = Array.isArray(risks) ? risks : [];
  const safeMitigations = Array.isArray(mitigations) ? mitigations : [];

  const criticalRisks = safeRisks.filter(r => r.severity === RiskSeverity.CRITICAL).length;
  const highRisks = safeRisks.filter(r => r.severity === RiskSeverity.HIGH).length;
  const activeRisks = safeRisks.filter(r => r.status !== RiskStatus.CLOSED).length;
  const mitigatedRisks = safeRisks.filter(r => r.status === RiskStatus.MITIGATING || r.status === RiskStatus.MONITORING).length;

  const riskColumns = [
    {
      title: 'Risk',
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record: Risk) => (
        <Space direction="vertical" size={0}>
          <Text strong>{title}</Text>
          {record.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>{record.description.substring(0, 60)}...</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Category',
      key: 'category',
      render: (_: any, record: Risk) => {
        const cat = (record as any).riskCategory || (record as any).category;
        return <Tag color={cat?.color || 'blue'}>{cat?.name || 'N/A'}</Tag>;
      },
    },
    {
      title: 'Probability',
      dataIndex: 'probability',
      key: 'probability',
      render: (prob: number) => (
        <Tag color={getDecimalColor(prob)}>{getProbabilityLabel(prob)}</Tag>
      ),
    },
    {
      title: 'Impact',
      dataIndex: 'impact',
      key: 'impact',
      render: (impact: number) => (
        <Tag color={getDecimalColor(impact)}>{getImpactLabel(impact)}</Tag>
      ),
    },
    {
      title: 'Score',
      dataIndex: 'riskScore',
      key: 'riskScore',
      sorter: (a: Risk, b: Risk) => a.riskScore - b.riskScore,
      render: (score: number) => (
        <Badge count={score} style={{ backgroundColor: score >= 15 ? '#f5222d' : score >= 9 ? '#faad14' : '#52c41a' }} />
      ),
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (severity: RiskSeverity) => (
        <Tag color={getSeverityColor(severity)}>{severity}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: RiskStatus) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: 'Schedule Impact',
      key: 'scheduleImpact',
      render: (_: any, record: Risk) => {
        const linked: string[] =
          Array.isArray((record as any).linkedTaskIds) ? (record as any).linkedTaskIds
            : Array.isArray((record as any).linked_task_ids) ? (record as any).linked_task_ids
              : Array.isArray((record as any).taskIds) ? (record as any).taskIds
                : [];
        const delay = Number((record as any).scheduleImpactDays ?? (record as any).delayDays ?? (record as any).delay_days ?? 0);
        if (!linked.length && delay <= 0) return <Text type="secondary">—</Text>;
        const linkedNames = linked
          .map((id) => scheduleTasks.find((t) => t.id === id)?.name)
          .filter(Boolean)
          .slice(0, 2)
          .join(', ');
        return (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: 12, color: '#faad14' }}>
              +{Math.max(0, delay)} day{Math.max(0, delay) !== 1 ? 's' : ''}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {linked.length > 0 ? (linkedNames || `${linked.length} task(s) linked`) : 'No task linked'}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'AI Suggested',
      key: 'aiSuggested',
      width: 260,
      render: (_: any, record: Risk) => {
        const r: any = record as any;
        if (!hasAiSuggestion(record)) return <Text type="secondary">—</Text>;

        const aiSeverity = r.aiSeverity as RiskSeverity | undefined;
        const aiConfidence = typeof r.aiConfidence === 'number' ? (r.aiConfidence as number) : null;
        const aiReasons = r.aiReasons;
        const reasonsText =
          typeof aiReasons === 'string'
            ? aiReasons
            : aiReasons
              ? (() => {
                  try {
                    return JSON.stringify(aiReasons);
                  } catch {
                    return String(aiReasons);
                  }
                })()
              : '';

        return (
          <Space direction="vertical" size={4}>
            <Tag color={aiSeverity ? getSeverityColor(aiSeverity) : 'default'}>{aiSeverity || '—'}</Tag>
            <AIConfidenceBadge confidence={aiConfidence} />
            {reasonsText ? (
              <Text type="secondary" style={{ fontSize: 12, maxWidth: 220 }}>
                {reasonsText.length > 80 ? `${reasonsText.substring(0, 80)}…` : reasonsText}
              </Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>No reasons provided</Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'AI Actions',
      key: 'aiActions',
      width: 180,
      render: (_: any, record: Risk) => {
        if (!hasAiSuggestion(record)) return null;
        const reviewed = !!aiReviewedRiskIds[record.id];
        if (reviewed) return <Tag color="green">Reviewed</Tag>;

        return (
          <Space size={8}>
            <Button size="small" onClick={() => handleApplyAiSuggestion(record)} disabled={!can('MANAGER_AND_ABOVE')}>
              Apply
            </Button>
            <Button size="small" onClick={() => handleKeepBaseline(record)}>
              Keep
            </Button>
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_: any, record: Risk) => {
        const menuItems: { key: string; label: string; icon?: React.ReactNode; danger?: boolean; onClick: () => void }[] = [];
        if (can('ENGINEER_AND_ABOVE')) menuItems.push({ key: 'edit', label: 'Edit', icon: <EditOutlined />, onClick: () => handleEditRisk(record) });
        if (can('MANAGER_AND_ABOVE')) {
          menuItems.push({ key: 'assess', label: 'Assess', icon: <AuditOutlined />, onClick: () => handleAssessRisk(record) });
          menuItems.push({ key: 'mitigate', label: 'Add mitigation', icon: <CheckCircleOutlined />, onClick: () => handleAddMitigation(record) });
          menuItems.push({ key: 'escalate', label: 'Escalate', icon: <FlagOutlined />, danger: true, onClick: () => handleEscalateRisk(record) });
          menuItems.push({ key: 'delete', label: 'Delete', icon: <DeleteOutlined />, danger: true, onClick: () => handleDeleteRisk(record.id) });
        }
        if (menuItems.length === 0) return null;
        return (
          <Dropdown
            menu={{ items: menuItems.map(({ key, label, icon, danger, onClick }) => ({ key, label, icon, danger, onClick })) }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button type="text" size="small" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const mitigationColumns = [
    {
      title: 'Risk',
      key: 'risk',
      render: (_: any, record: Mitigation) => {
        const risk = safeRisks.find(r => r.id === record.riskId);
        return risk?.title || 'Unknown Risk';
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action: string, record: Mitigation) => (
        <Space direction="vertical" size={0}>
          <Text strong>{action}</Text>
          {record.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>{record.description.substring(0, 50)}...</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: MitigationStatus) => {
        const colors = {
          [MitigationStatus.PLANNED]: 'blue',
          [MitigationStatus.IN_PROGRESS]: 'orange',
          [MitigationStatus.COMPLETED]: 'green',
          [MitigationStatus.CANCELLED]: 'red',
        };
        return <Tag color={colors[status]}>{status}</Tag>;
      },
    },
    {
      title: 'Target Date',
      dataIndex: 'targetDate',
      key: 'targetDate',
      render: (date: string) => date ? dayjs(date).format('MMM DD, YYYY') : '-',
    },
    {
      title: 'Cost',
      key: 'cost',
      render: (_: any, record: Mitigation) => (
        <Text>
          ${record.actualCost || record.estimatedCost || 0}
        </Text>
      ),
    },
    {
      title: 'Effectiveness',
      dataIndex: 'effectiveness',
      key: 'effectiveness',
      render: (effectiveness: string) => {
        if (!effectiveness) return '-';
        const colors: Record<string, string> = { LOW: 'orange', MEDIUM: 'blue', HIGH: 'green' };
        return <Tag color={colors[effectiveness] || 'default'}>{effectiveness}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Mitigation) => (
        can('MANAGER_AND_ABOVE') ? (
          <Button
            type="link"
            danger
            size="small"
            onClick={() => handleDeleteMitigation(record.id)}
          >
            Delete
          </Button>
        ) : null
      ),
    },
  ];

  const riskTabItems = [
    { key: 'risks',       icon: <SafetyOutlined />,     label: 'Risks'       },
    { key: 'mitigations', icon: <CheckCircleOutlined />, label: 'Mitigations' },
    { key: 'matrix',      icon: <DashboardOutlined />,   label: 'Risk Matrix' },
    { key: 'overview',    icon: <FileTextOutlined />,    label: 'Overview'    },
  ];

  return (
    <>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 24, alignItems: 'flex-start' }}>

        {/* ── Main column ── */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 24 }}>

          <div>
            <Title level={2} style={{ marginBottom: 4 }}>Risk Management</Title>
            <Text type="secondary" style={{ fontSize: 14 }}>Log risks, add mitigations, and track severity. Select a project to get started.</Text>
          </div>

          <div style={{ background: '#1a1a1a', border: '1px solid #333333', borderRadius: 8, padding: 16, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <Text style={{ display: 'block', marginBottom: 8, fontSize: 13, color: '#9ca3af' }}>Project</Text>
              <ProjectSelector />
            </div>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>Refresh</Button>
          </div>

          {!selectedProject && !projectsLoading && (
            <Alert
              message="Choose a project first"
              description="Select a project above to add risks, mitigation actions, and view the risk matrix."
              type="info"
              showIcon
              style={{
                background: 'rgba(59,130,246,0.08)',
                border: '1px solid rgba(59,130,246,0.25)',
                borderRadius: 8,
              }}
            />
          )}

          {selectedProject && (
            <Spin spinning={loading}>

              {/* Monitoring Summary Card */}
              {monitoring && monitoring.summary && (
                <Card title="Risk Monitoring Summary" style={{ marginBottom: 16 }}>
                  <Row gutter={[16, 16]}>
                    <Col xs={12} md={6}>
                      <Statistic title="Total" value={monitoring.summary.total ?? 0} valueStyle={{ color: '#1890ff' }} />
                    </Col>
                    <Col xs={12} md={6}>
                      <Statistic title="Open" value={monitoring.summary.open ?? 0} valueStyle={{ color: '#faad14' }} />
                    </Col>
                    <Col xs={12} md={6}>
                      <Statistic title="Closed" value={monitoring.summary.closed ?? 0} valueStyle={{ color: '#52c41a' }} />
                    </Col>
                    <Col xs={12} md={6}>
                      <Statistic title="Open/Closed Ratio" value={monitoring.summary.openClosedRatio ?? '0.00'} />
                    </Col>
                  </Row>
                </Card>
              )}

              {/* Custom tab nav */}
              <div style={{ borderBottom: '1px solid #333333' }}>
                <div style={{ display: 'flex' }}>
                  {riskTabItems.map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '14px 4px', marginRight: 28,
                        background: 'transparent', border: 'none',
                        borderBottom: activeTab === tab.key ? '2px solid #009944' : '2px solid transparent',
                        color: activeTab === tab.key ? '#009944' : '#9ca3af',
                        fontFamily: 'inherit', fontSize: 14, fontWeight: 500,
                        cursor: 'pointer', transition: 'color 0.2s, border-color 0.2s', marginBottom: -1,
                      }}
                    >
                      {tab.icon} {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab content panel */}
              <div style={{ background: '#1a1a1a', border: '1px solid #333333', borderTop: 'none', borderRadius: '0 0 6px 6px', overflow: 'hidden' }}>

                {/* Risks */}
                {activeTab === 'risks' && (
                  <>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #333333', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text type="secondary" style={{ fontSize: 13 }}>Potential issues that could affect the project. Use the ⋮ menu on each row to assess, mitigate, or escalate.</Text>
                      <Space>
                        {can('MANAGER_AND_ABOVE') && (
                          <Button
                            icon={<RobotOutlined />}
                            loading={aiScoringLoading}
                            onClick={handleRunAiRiskScoring}
                            style={{ background: '#1f6feb', borderColor: '#1f6feb', color: '#ffffff' }}
                          >
                            Run AI Risk Scoring
                          </Button>
                        )}
                        {can('ENGINEER_AND_ABOVE') && (
                          <Button
                            icon={<PlusOutlined />}
                            onClick={handleAddRisk}
                            style={{ background: '#00aaff', borderColor: '#00aaff', color: '#ffffff' }}
                          >
                            Add risk
                          </Button>
                        )}
                      </Space>
                    </div>
                    <Table
                      columns={riskColumns}
                      dataSource={safeRisks}
                      rowKey="id"
                      pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} risk${t !== 1 ? 's' : ''}` }}
                      scroll={{ x: 1400 }}
                      locale={{ emptyText: 'No risks yet. Click "Add risk" to log the first risk (e.g. weather delay, material shortage).' }}
                    />
                  </>
                )}

                {/* Mitigations */}
                {activeTab === 'mitigations' && (
                  <>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #333333', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text type="secondary" style={{ fontSize: 13 }}>Actions taken to reduce or respond to risks.</Text>
                      {can('MANAGER_AND_ABOVE') && (
                        <Button icon={<PlusOutlined />} onClick={() => handleAddMitigation()}
                          style={{ background: '#00aaff', borderColor: '#00aaff', color: '#ffffff' }}>
                          Add mitigation
                        </Button>
                      )}
                    </div>
                    <Table
                      columns={mitigationColumns}
                      dataSource={safeMitigations}
                      rowKey="id"
                      pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} mitigation${t !== 1 ? 's' : ''}` }}
                      scroll={{ x: 1000 }}
                      locale={{ emptyText: 'No mitigations yet. Add a risk first, then use "Add mitigation" or the risk menu to link an action.' }}
                    />
                  </>
                )}

                {/* Risk Matrix */}
                {activeTab === 'matrix' && (
                  <div style={{ padding: 16 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>Risks plotted by probability and impact. Focus on high-probability, high-impact items first.</Text>
                    {riskMatrix && <RiskMatrix data={riskMatrix} />}
                    {!riskMatrix && <Alert message="No matrix data yet" description="Add risks with probability and impact set to see the risk matrix." type="info" showIcon />}
                  </div>
                )}

                {/* Overview */}
                {activeTab === 'overview' && (
                  <div style={{ padding: 16 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>Summary of risks by severity and top items to watch.</Text>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} lg={12}>
                        <Card title="By severity">
                          {monitoring && Object.keys(monitoring.bySeverity || {}).length > 0 ? (
                            <Space direction="vertical" style={{ width: '100%' }}>
                              {Object.entries(monitoring.bySeverity || {}).map(([severity, count]) => (
                                <div key={severity}>
                                  <Text>{severity}: {count as number}</Text>
                                  <Progress
                                    percent={monitoring.summary.total > 0 ? ((count as number) / monitoring.summary.total) * 100 : 0}
                                    strokeColor={getSeverityColor(severity as RiskSeverity)}
                                    showInfo={false}
                                  />
                                </div>
                              ))}
                            </Space>
                          ) : (
                            <Text type="secondary">No severity breakdown yet. Add risks to see distribution.</Text>
                          )}
                        </Card>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Card title="Top risks">
                          {monitoring?.topRisks && monitoring.topRisks.length > 0 ? (
                            monitoring.topRisks.map((risk, index) => (
                              <div key={risk.id} style={{ marginBottom: 12 }}>
                                <Badge count={index + 1} style={{ marginRight: 8 }} />
                                <Text strong>{risk.title}</Text>
                                <Tag color={getSeverityColor(risk.severity as RiskSeverity)} style={{ marginLeft: 8 }}>
                                  Score: {risk.riskScore}
                                </Tag>
                              </div>
                            ))
                          ) : (
                            <Text type="secondary">No high-priority risks. Add risks and set probability/impact to see top items here.</Text>
                          )}
                        </Card>
                      </Col>
                    </Row>
                  </div>
                )}

              </div>
            </Spin>
          )}
        </div>

        {/* ── Quick Stats sidebar ── */}
          <div style={{ width: isMobile ? '100%' : 240, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12, marginTop: isMobile ? 0 : 88 }}>
            <Text style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Quick Stats
            </Text>
            {[
              { icon: <ExclamationCircleOutlined />, iconBg: 'rgba(0,170,255,0.12)',  iconColor: '#00aaff', label: 'Total Risks',    value: safeRisks.length,          valueColor: '#00aaff' },
              { icon: <WarningOutlined />,           iconBg: 'rgba(255,0,64,0.12)',   iconColor: '#ff0040', label: 'Critical/High',  value: criticalRisks + highRisks, valueColor: '#ff0040' },
              { icon: <RiseOutlined />,              iconBg: 'rgba(255,170,0,0.12)',  iconColor: '#ffaa00', label: 'Active Risks',   value: activeRisks,               valueColor: '#ffaa00' },
              { icon: <CheckCircleOutlined />,       iconBg: 'rgba(0,153,68,0.12)',   iconColor: '#009944', label: 'Mitigated',      value: mitigatedRisks,            valueColor: '#009944' },
            ].map((stat, i) => (
              <div key={i} style={{ background: '#1a1a1a', border: '1px solid #333333', borderRadius: 6, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: stat.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: stat.iconColor, fontSize: 20, flexShrink: 0 }}>
                  {stat.icon}
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{stat.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: stat.valueColor, lineHeight: 1 }}>{stat.value}</div>
                </div>
              </div>
            ))}
          </div>
      </div>

      {/* Risk Modal */}
      <Modal
        title={editingRisk ? 'Edit risk' : 'Add risk'}
        open={riskModalVisible}
        onOk={handleRiskSubmit}
        onCancel={() => {
          setRiskModalVisible(false);
          riskForm.resetFields();
        }}
        width={640}
        okText={editingRisk ? 'Save changes' : 'Add risk'}
      >
        <Form form={riskForm} layout="vertical">
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Enter a short title' }]}
          >
            <Input placeholder="e.g. Material price increase, Weather delay" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Describe the risk' }]}
          >
            <TextArea rows={3} placeholder="What could go wrong and how it might affect the project?" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Select status' }]}
              >
                <Select placeholder="Current status">
                  {Object.values(RiskStatus).map(status => (
                    <Option key={status} value={status}>{status}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="severity" label="Severity" tooltip="Overall severity (can be derived from probability × impact)">
                <Select placeholder="Low / Medium / High / Critical" allowClear>
                  {Object.values(RiskSeverity).map(s => (
                    <Option key={s} value={s}>{s}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="probability"
                label="Probability"
                rules={[{ required: true, message: 'Select likelihood' }]}
                tooltip="How likely is this to happen?"
              >
                <Select placeholder="Likelihood">
                  <Option value={0.1}>Very low</Option>
                  <Option value={0.25}>Low</Option>
                  <Option value={0.5}>Medium</Option>
                  <Option value={0.75}>High</Option>
                  <Option value={1.0}>Very high</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="impact"
                label="Impact"
                rules={[{ required: true, message: 'Select impact' }]}
                tooltip="How much would it affect cost, schedule, or quality?"
              >
                <Select placeholder="Impact if it occurs">
                  <Option value={0.1}>Very low</Option>
                  <Option value={0.25}>Low</Option>
                  <Option value={0.5}>Medium</Option>
                  <Option value={0.75}>High</Option>
                  <Option value={1.0}>Very high</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="responseStrategy" label="Response strategy" tooltip="How you plan to deal with it">
            <Select placeholder="Avoid / Mitigate / Transfer / Accept" allowClear>
              <Option value="AVOID">Avoid</Option>
              <Option value="MITIGATE">Mitigate</Option>
              <Option value="TRANSFER">Transfer</Option>
              <Option value="ACCEPT">Accept</Option>
            </Select>
          </Form.Item>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="linkedTaskIds"
                label="Linked schedule task(s)"
                tooltip="Select task(s) impacted by this risk so Schedule/Gantt can show delays."
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select impacted task(s)"
                  options={scheduleTasks.map((t) => ({ value: t.id, label: t.name }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="scheduleImpactDays"
                label="Delay impact (days)"
                tooltip="Estimated delay this risk adds to linked tasks."
                initialValue={0}
              >
                <InputNumber min={0} step={1} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Assess Risk Modal */}
      <Modal
        title="Assess risk"
        open={assessModalVisible}
        onOk={handleAssessSubmit}
        onCancel={() => {
          setAssessModalVisible(false);
          assessForm.resetFields();
        }}
        okText="Save assessment"
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>Update probability and impact after mitigation or new information.</Text>
        <Form form={assessForm} layout="vertical">
          <Form.Item
            name="residualProbability"
            label="Residual probability"
            rules={[{ required: true, message: 'Select probability' }]}
          >
            <Select placeholder="Likelihood after controls">
              <Option value={0.1}>Very low</Option>
              <Option value={0.25}>Low</Option>
              <Option value={0.5}>Medium</Option>
              <Option value={0.75}>High</Option>
              <Option value={1.0}>Very high</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="residualImpact"
            label="Residual impact"
            rules={[{ required: true, message: 'Select impact' }]}
          >
            <Select placeholder="Impact after controls">
              <Option value={0.1}>Very low</Option>
              <Option value={0.25}>Low</Option>
              <Option value={0.5}>Medium</Option>
              <Option value={0.75}>High</Option>
              <Option value={1.0}>Very high</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Escalate Risk Modal */}
      <Modal
        title="Escalate risk"
        open={escalateModalVisible}
        onOk={handleEscalateSubmit}
        onCancel={() => {
          setEscalateModalVisible(false);
          escalateForm.resetFields();
        }}
        okText="Escalate"
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>Escalate to higher authority when the risk is outside your control or budget.</Text>
        <Form form={escalateForm} layout="vertical">
          <Form.Item
            name="level"
            label="Escalation level"
            rules={[{ required: true, message: 'Select level' }]}
          >
            <Select placeholder="Who should this go to?">
              {Object.values(EscalationLevel).map(level => (
                <Option key={level} value={level}>{level}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Explain why you are escalating' }]}
          >
            <TextArea rows={4} placeholder="Why does this need escalation? What support or decision do you need?" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Mitigation Modal */}
      <Modal
        title="Add mitigation"
        open={mitigationModalVisible}
        onOk={handleMitigationSubmit}
        onCancel={() => {
          setMitigationModalVisible(false);
          mitigationForm.resetFields();
        }}
        width={560}
        okText="Add mitigation"
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>Define an action to reduce or respond to the risk.</Text>
        <Form form={mitigationForm} layout="vertical">
          <Form.Item
            name="riskId"
            label="Risk"
            rules={[{ required: true, message: 'Select the risk' }]}
          >
            <Select placeholder="Which risk is this for?" showSearch optionFilterProp="children">
              {safeRisks.map(risk => (
                <Option key={risk.id} value={risk.id}>{risk.title}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="action"
            label="Action"
            rules={[{ required: true, message: 'Enter the action' }]}
          >
            <Input placeholder="e.g. Order materials early, Add buffer to schedule" />
          </Form.Item>

          <Form.Item name="description" label="Description (optional)">
            <TextArea rows={2} placeholder="More details" />
          </Form.Item>

          <Form.Item
            name="strategy"
            label="Strategy"
            rules={[{ required: true, message: 'Describe the approach' }]}
          >
            <TextArea rows={2} placeholder="How will this reduce the risk?" />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Select status' }]}
          >
            <Select placeholder="Planned / In progress / Completed">
              {Object.values(MitigationStatus).map(status => (
                <Option key={status} value={status}>{status.replace('_', ' ')}</Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="startDate" label="Start date">
                <DatePicker style={{ width: '100%' }} placeholder="When started" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="targetDate" label="Target date">
                <DatePicker style={{ width: '100%' }} placeholder="Due by" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="estimatedCost" label="Estimated cost (₱)">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="effectiveness" label="Effectiveness">
                <Select placeholder="Low / Medium / High" allowClear>
                  <Option value="LOW">Low</Option>
                  <Option value="MEDIUM">Medium</Option>
                  <Option value="HIGH">High</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="assignedToId" label="Assigned to">
            <Select placeholder="Optional" allowClear showSearch optionFilterProp="label"
              options={users.map(u => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ProjectRisk;
